/**
 * timeContext — Single source of truth for "what time is it for the learner?".
 *
 * Audit ref: LEARNER_HOME_FLOW_UX_AUDIT_2026 §P2-5, P2-6.
 *
 * Before this helper, four places in the learner shell each made their
 * own time-of-day decisions:
 *   - HomePage greeting (had a dode tak: `hour<8` and `hour<12` both
 *     mapped to 'Goedemorgen', and 'Goedenacht' didn't exist)
 *   - Sidebar Djay sleepy window  (`hour < 8 || hour > 20`)
 *   - MobileNav Djay sleepy window (`hour < 8 || hour > 20`)
 *   - FloatingDjay only used time-based easter eggs, no day/night emotie
 *
 * Now they all flow through `getTimeContext()` so behavior stays in sync.
 */

export type TimePeriod = 'night' // 0-5
| 'morning' // 6-11
| 'afternoon' // 12-17
| 'evening' // 18-20
| 'late'; // 21-23

export interface TimeContext {
  /** 0-23, the hour the context was computed for. */
  hour: number;
  /** Coarse period label. */
  period: TimePeriod;
  /** Greeting the learner sees ("Goedemorgen", etc.). */
  greeting: string;
  /**
   * Whether Djay should look sleepy for this hour.
   * True for `night` (0-5) and `late` (21-23) — kept aligned with the
   * pre-existing Sidebar/MobileNav window (`hour < 8 || hour > 20`)
   * minus the 6-7 slot (which now reads 'Goedemorgen' but with a wakeful Djay).
   */
  djaySleepy: boolean;
}

/**
 * Compute a unified time-context for the current moment.
 * Pass an explicit Date for tests; defaults to `new Date()`.
 */
export function getTimeContext(date: Date = new Date()): TimeContext {
  const hour = date.getHours();
  let period: TimePeriod;
  let greeting: string;
  let djaySleepy = false;
  if (hour < 6) {
    period = 'night';
    greeting = 'Goedenacht';
    djaySleepy = true;
  } else if (hour < 12) {
    period = 'morning';
    greeting = 'Goedemorgen';
  } else if (hour < 18) {
    period = 'afternoon';
    greeting = 'Goedemiddag';
  } else if (hour < 21) {
    period = 'evening';
    greeting = 'Goedenavond';
  } else {
    period = 'late';
    greeting = 'Goedenavond';
    djaySleepy = true;
  }
  return {
    hour,
    period,
    greeting,
    djaySleepy
  };
}